// View models built from the workflow engine's reads and IO's Dossier.

export enum ProductionDeviationStep {
  ConfirmFindings = 'confirm_findings',
  ApproveFindings = 'approve_findings',
  ExecuteActions = 'execute_actions',
  Closed = 'end_findings',
}

export enum ProductionDeviationRole {
  IoAnalyst = 'analyst',
  IoProductionEngineer = 'production-engineer',
  IoAdmin = 'admin',
  IoFieldSpecialist = 'field-specialist',
  /** Can see the workflow but cannot act on it. */
  IoUser = 'user',
}

export enum ProductionDeviationAssessment {
  MeasurementIssue = 'MeasurementIssue',
  AllocationInputIssue = 'AllocationInputIssue',
  RoutingConstraint = 'RoutingConstraint',
  Undetermined = 'Undetermined',
}

export enum ProductionDeviationVerdict {
  Confirmed = 'Confirmed',
  Rejected = 'Rejected',
}

/** `all_done` and `incomplete` are the engine's for-each outcomes that end a UC1 case. */
export type ProductionDeviationOutcome = 'dismissed' | 'all_done' | 'incomplete';

export type ProductionDeviationActionOutcome = 'done' | 'not_applicable' | 'cannot_complete';

export type ProductionDeviationAuditKind =
  | 'case-opened'
  | 'owner-claimed'
  | 'step-entered'
  | 'owner-returned'
  | 'actions-resolved'
  | 'action-completed'
  | 'case-closed';

export interface ProductionDeviationCandidate {
  candidateId: string;
  rank: number;
  title: string;
  family: ProductionDeviationAssessment;
  /** Per-candidate confidence, 0-100. */
  confidence: number;
  rationale: string;
}

/** Typed recommended action; candidateId null means it applies whichever candidate is selected. */
export interface ProductionDeviationActionItem {
  id: string;
  candidateId: string | null;
  targetRole: ProductionDeviationRole;
  description: string;
  /** What the attendee must confirm before the item can complete. */
  completionCriteria: string[];
}

/** The lean open-time slice of the detection result the deciders read. */
export interface ProductionDeviationCaseContext {
  summary: string;
  stream: string;
  product: string;
  area: string;
}

export interface ProductionDeviationFindings {
  verdict: ProductionDeviationVerdict | null;
  assessment: ProductionDeviationAssessment | null;
  selectedCandidateIds: string[];
  /** Why the analyst dismissed the case; null unless it was rejected. */
  rejectionReason: string | null;
  defectOwner: ProductionDeviationRole | null;
  defectDisposition: string | null;
}

export interface ProductionDeviationCompletion {
  outcome: ProductionDeviationActionOutcome | null;
  reason: string | null;
  note: string;
  by: string;
  atUtc: string;
}

export interface ProductionDeviationAuditEntry {
  id: number;
  atUtc: string;
  kind: ProductionDeviationAuditKind;
  actor: string;
  step: ProductionDeviationStep;
  text: string;
}

export interface ProductionDeviationCase {
  engineCaseId: string;
  workflowVersion: number;
  contentHash: string;
  step: ProductionDeviationStep;
  owner: string | null;
  /** Concrete role the current owner claimed the step under. */
  ownerRole: ProductionDeviationRole | null;
  /** The analyst a returned case goes back to, instead of the role queue. */
  priorOwner: string | null;
  returnReason: string | null;
  dataForDate: string;
  detectedUtc: string;
  context: ProductionDeviationCaseContext;
  candidates: ProductionDeviationCandidate[];
  actionItems: ProductionDeviationActionItem[];
  proposedAssessment: ProductionDeviationAssessment;
  findings: ProductionDeviationFindings;
  /** Item ids resolved on entering execute_actions; null until then, sealed afterwards. */
  resolvedItemIds: string[] | null;
  completions: Record<string, ProductionDeviationCompletion>;
  outcome: ProductionDeviationOutcome | null;
  audit: ProductionDeviationAuditEntry[];
}
